(() => {
  const coordinates = (value) => {
    if (!value) return null;
    try {
      const url = new URL(value, window.location.origin);
      const raw = url.searchParams.get('destination') || url.searchParams.get('query') || '';
      const match = raw.match(/(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)/);
      const lat = Number(match?.[1]);
      const lng = Number(match?.[2]);
      return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
    } catch (_error) {
      return null;
    }
  };

  const load = (apiKey) => {
    if (window.google?.maps) return Promise.resolve(window.google.maps);
    if (window.__letoileGoogleMapsPromise) return window.__letoileGoogleMapsPromise;
    window.__letoileGoogleMapsPromise = new Promise((resolve, reject) => {
      const callback = `__letoileGoogleMapsReady${Date.now()}`;
      const cleanup = () => {
        delete window[callback];
        window.__letoileGoogleMapsPromise = null;
      };
      const timeout = window.setTimeout(() => {
        cleanup();
        reject(new Error('Google Maps timed out'));
      }, 15000);
      window[callback] = () => {
        window.clearTimeout(timeout);
        delete window[callback];
        resolve(window.google.maps);
      };
      const script = document.createElement('script');
      script.async = true;
      script.defer = true;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&v=weekly&loading=async&callback=${callback}`;
      script.onerror = () => {
        window.clearTimeout(timeout);
        cleanup();
        reject(new Error('Google Maps failed to load'));
      };
      document.head.append(script);
    });
    return window.__letoileGoogleMapsPromise;
  };

  const createFallbackPins = (container, cards, onSelect) => {
    if (!container) return [];
    container.replaceChildren();
    return cards.map((card, index) => {
      const pin = document.createElement('button');
      pin.className = `store-locator__pin store-locator__pin--${index + 1}`;
      pin.type = 'button';
      pin.dataset.storeLocatorPin = '';
      pin.dataset.storeIndex = String(index);
      pin.setAttribute('aria-label', card.querySelector('[data-store-locator-location]')?.textContent || '');
      const label = document.createElement('span');
      label.textContent = String(index + 1);
      pin.append(label);
      pin.addEventListener('click', () => onSelect(index, true, true));
      container.append(pin);
      return pin;
    });
  };

  const setState = (messageElement, retryElement, message, retry) => {
    if (messageElement) messageElement.textContent = message;
    if (retryElement) retryElement.hidden = !retry;
  };

  const init = async ({ apiKey, cards, element, onSelect }) => {
    const maps = await load(apiKey);
    const positions = cards.map((card) => coordinates(card.dataset.storeMapUrl));
    if (!positions.some(Boolean)) throw new Error('Store coordinates are missing');

    const map = new maps.Map(element, {
      center: { lat: 24.7, lng: 55.5 },
      zoom: 8,
      clickableIcons: false,
      fullscreenControl: false,
      mapTypeControl: false,
      streetViewControl: false,
      gestureHandling: 'cooperative',
      styles: [
        { featureType: 'poi', stylers: [{ visibility: 'off' }] },
        { featureType: 'transit', stylers: [{ visibility: 'off' }] },
      ],
    });

    class StoreMarker extends maps.OverlayView {
      constructor(position, index, label) {
        super();
        this.position = position;
        this.index = index;
        this.label = label;
        this.element = null;
      }

      onAdd() {
        const button = document.createElement('button');
        button.className = 'store-locator__google-pin';
        button.type = 'button';
        button.setAttribute('aria-label', this.label);
        const number = document.createElement('span');
        number.textContent = String(this.index + 1);
        button.append(number);
        button.addEventListener('click', () => onSelect(this.index));
        this.element = button;
        this.getPanes().overlayMouseTarget.append(button);
      }

      draw() {
        const point = this.getProjection().fromLatLngToDivPixel(this.position);
        if (!point || !this.element) return;
        this.element.style.left = `${point.x}px`;
        this.element.style.position = 'absolute';
        this.element.style.top = `${point.y}px`;
        this.element.style.transform = 'translate(-50%, -100%) rotate(-45deg)';
      }

      onRemove() {
        this.element?.remove();
        this.element = null;
      }

      setSelected(selected) {
        this.element?.classList.toggle('is-selected', selected);
      }
    }

    const markers = positions.map((position, index) => {
      if (!position) return null;
      const label = cards[index].querySelector('[data-store-locator-location]')?.textContent || '';
      const marker = new StoreMarker(position, index, label);
      marker.setMap(map);
      return marker;
    });

    const fit = (visible = positions.map(Boolean)) => {
      const bounds = new maps.LatLngBounds();
      positions.forEach((position, index) => {
        if (position && visible[index]) bounds.extend(position);
      });
      if (!bounds.isEmpty()) map.fitBounds(bounds, 56);
    };
    fit();

    return {
      select(index, focus) {
        markers.forEach((marker, markerIndex) => marker?.setSelected(markerIndex === index));
        if (focus && positions[index]) {
          map.panTo(positions[index]);
          map.setZoom(14);
        }
      },
      setVisible(visible) {
        markers.forEach((marker, index) => marker?.setMap(visible[index] ? map : null));
        fit(visible);
      },
    };
  };

  window.LEStoreGoogleMap = { createFallbackPins, init, setState };
})();
